import {useTranslation} from '@/translations/i18n'
import {AnswerField, Content} from '../atoms/Atoms'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {forwardRef, Fragment} from 'react'
import {StyledActivityImage, StyledActivityTitle} from './style'
import {RemapExperienceForViewOnly} from '@/features/experience-view-only/types'

export const MerchandisingStep = forwardRef<HTMLDivElement, {experience: RemapExperienceForViewOnly}>(
    ({experience}, ref) => {
        const {t} = useTranslation()

        return (
            <Flexbox direction="column" fullWidth ref={ref}>
                <Content
                    title={t('experience_shell:step_merchandising')}
                    sectionTitle={t('vetting:merch:experience:input_shell_form_title')}
                >
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:first_name_label')}
                        hasValue={!!experience.host.firstName}
                        value={experience.host.firstName ?? undefined}
                    />
                    <AnswerField
                        labelTitle={t('experience_view_only:bio')}
                        hasValue={!!experience.expertise.answers.expertise_summary?.answer}
                        value={experience.expertise.answers.expertise_summary?.answer}
                    />
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:recognition_general_label')}
                        hasValue={!!experience.expertise.answers.recognition_summary?.answer}
                        value={experience.expertise.answers.recognition_summary?.answer}
                    />
                </Content>

                <Divider direction="horizontal" />

                <Content>
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:listing_title_label')}
                        hasValue={!!experience.expertise.answers.listing_title?.answer}
                        value={experience.expertise.answers.listing_title?.answer}
                    />
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:byline_label')}
                        hasValue={!!experience.expertise.answers.byline?.answer}
                        value={experience.expertise.answers.byline?.answer}
                    />
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:location_description_label')}
                        hasValue={!!experience.expertise.answers.location_description?.answer}
                        value={experience.expertise.answers.location_description?.answer}
                    />
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:what_guest_will_do_label')}
                        hasValue={!!experience.expertise.answers.guest_activities?.answer}
                        value={experience.expertise.answers.guest_activities?.answer}
                    />
                </Content>

                <Content sectionTitle={t('experience_view_only:content_sent_to_host')} columns={2}>
                    {/* expertise title */}
                    <AnswerField
                        labelTitle={t('experience_view_only:title')}
                        hasValue={!!experience.expertise.answers.host_experience_title?.answer}
                        value={experience.expertise.answers.host_experience_title?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_view_only:improved_title')}
                        hasValue={!!experience.expertise.answers.improved_experience_title?.answer}
                        value={experience.expertise.answers.improved_experience_title?.answer}
                    />

                    {/* expertise description (bio) */}
                    <AnswerField
                        labelTitle={t('experience_view_only:bio')}
                        hasValue={!!experience.expertise.answers.host_expertise_description?.answer}
                        value={experience.expertise.answers.host_expertise_description?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_view_only:improved_bio')}
                        hasValue={!!experience.expertise.answers.improved_host_expertise_description?.answer}
                        value={experience.expertise.answers.improved_host_expertise_description?.answer}
                    />

                    {/* recognition title */}
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:recognition_title_label')}
                        hasValue={!!experience.expertise.answers.host_recognition_title?.answer}
                        value={experience.expertise.answers.host_recognition_title?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_view_only:improved_recognition')}
                        hasValue={!!experience.expertise.answers.improved_host_recognition_title?.answer}
                        value={experience.expertise.answers.improved_host_recognition_title?.answer}
                    />

                    {/* listing title */}
                    <AnswerField
                        labelTitle={t('vetting:merch:experience:listing_title_label')}
                        hasValue={!!experience.expertise.answers.experience_title?.answer}
                        value={experience.expertise.answers.experience_title?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_view_only:improved_listing_title')}
                        hasValue={!!experience.expertise.answers.improved_experience_title?.answer}
                        value={experience.expertise.answers.improved_experience_title?.answer}
                    />

                    {/* listing description */}

                    <AnswerField
                        labelTitle={t('vetting:merch:experience:listing_description_label')}
                        hasValue={!!experience.expertise.answers.experience_description?.answer}
                        value={experience.expertise.answers.experience_description?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_view_only:improved_listing_description')}
                        hasValue={!!experience.expertise.answers.improved_experience_description?.answer}
                        value={experience.expertise.answers.improved_experience_description?.answer}
                    />
                    <Divider direction="horizontal" />
                    <Divider direction="horizontal" />
                </Content>

                {experience.itineraries.map((itinerary, index) => (
                    <Fragment key={itinerary.id}>
                        <Content columns={1}>
                            <StyledActivityTitle>
                                {t('vetting:merch:experience:activity_input_label', {index: index + 1})}
                            </StyledActivityTitle>

                            <StyledActivityImage src={itinerary?.coverImage?.url ?? ''} />
                        </Content>

                        <Content columns={2}>
                            {/* title */}
                            <AnswerField
                                labelTitle={t('experience_view_only:title')}
                                hasValue={!!itinerary.answers.title?.answer}
                                value={itinerary.answers.title?.answer}
                            />
                            <AnswerField
                                labelTitle={t('experience_view_only:improved_title')}
                                hasValue={!!itinerary.answers.improved_title?.answer}
                                value={itinerary.answers.improved_title?.answer}
                            />

                            {/* description */}
                            <AnswerField
                                labelTitle={t('experience_view_only:description')}
                                hasValue={!!itinerary.answers.description?.answer}
                                value={itinerary.answers.description?.answer}
                            />
                            <AnswerField
                                labelTitle={t('experience_view_only:improved_description')}
                                hasValue={!!itinerary.answers.improved_description?.answer}
                                value={itinerary.answers.improved_description?.answer}
                            />

                            {/* duration */}
                            <AnswerField
                                labelTitle={t('experience_view_only:duration')}
                                hasValue={!!itinerary.answers.duration?.answer}
                                value={t('experience_view_only:mins', {count: itinerary.answers.duration?.answer})}
                            />
                        </Content>
                    </Fragment>
                ))}
            </Flexbox>
        )
    }
)

MerchandisingStep.displayName = 'MerchandisingStep'
